/** Call mutations: request a human handoff. */

import { type UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { unwrap } from '../queries/_helpers';
import { queryKeys } from '../query-keys';
import { useApiClient } from '../react/ApiClientProvider';
import { type HandoffResponse, handoffResponseSchema } from '../responses';
import type { RequestHandoffBody } from './_types';

export interface RequestHandoffVars {
  callId: string;
  body?: RequestHandoffBody;
}

/**
 * `POST /calls/:callId/handoff` — escalate a live call to a human.
 *
 * NON-OPTIMISTIC: handoff is an escalation-routing action; the UI must wait for
 * the backend to confirm the new status before reflecting it. On success the
 * call detail is invalidated so the status badge refetches authoritatively.
 */
export function useRequestHandoff(
  tenantId: string,
  hotelId: string,
): UseMutationResult<HandoffResponse, unknown, RequestHandoffVars> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ callId, body }: RequestHandoffVars) => {
      const result = await client.POST(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/calls/{callId}/handoff',
        {
          params: { path: { tenantId, hotelId, callId } },
          body: body ?? {},
          parseAs: 'json',
        },
      );
      return unwrap(result, handoffResponseSchema);
    },
    onSuccess: (_data, { callId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.call(tenantId, hotelId, callId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.calls(tenantId, hotelId) });
    },
  });
}
